
import moment from 'moment';
import { Form, ProgressBar, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './FormHeader.css';
import { useNavigate, useParams } from 'react-router-dom';
import { BsTextLeft, BsViewStacked, BsEye } from "react-icons/bs";
import { BiCheck } from "react-icons/bi";
import { MdEditNote } from "react-icons/md";
import { AiOutlineFileWord } from "react-icons/ai";
import { useContext } from 'react';
import { FormTitleContext, SidebarContext } from '../../App'
import { FormStatus } from '../../core/constants/common';

export const FormHeader = ({ header, showWizard, totalQuestionCount, answeredQuestionCount, formData, navigateFormView, formStatus, isSinglePageView }) => {

	const navigate = useNavigate();
	const { setSidebarDisplay } = useContext(SidebarContext) || {};
	const { token, id, paramObj, langId } = useParams();
	const currentURL = window.location.href;

	const goto = (path) => {
		if (!id) return;
		let url = `/form_builder/${path}/${id}`;
		if (langId) url += `/${langId}`;
		navigate(url);
	};
	const isOpenForm = currentURL.includes('/open-form/') || currentURL.includes('/form_submit/');
	const isApprovalForm = currentURL.includes('/review/');
	const { formTitle } = useContext(FormTitleContext);
	const accessToken = localStorage.getItem("accessToken");

	const getHeader = () => {
		if (!formData?.assessment?.lastSubmissionDate) return `${header ?? ''}`;

		return `<span>Expiry :</span> ${moment(formData?.assessment?.lastSubmissionDate, 'YYYY-MM-DD').format("D MMMM, YYYY")}`;
	};

	const isSubmittedResponseView = () => {
		return formStatus === FormStatus.COMPLETED
			|| formStatus === FormStatus.CORRECTION_PENDING
			|| formStatus === FormStatus.PENDING_APPROVAL
			|| formStatus === FormStatus.COMPLETED;
	};

	const isSectionViewChecked = () => {
		return isSubmittedResponseView() ? !isSinglePageView : !currentURL.includes('/sp/') ? "checked" : "";
	}

	const isConsolidatedViewChecked = () => {
		return isSubmittedResponseView() ? isSinglePageView : currentURL.includes('/sp/') ? "checked" : "";
	}

	const handleDownloadWordDoc = () => {
		const contentNode = document.querySelector('.FormMainBody_main');
		if (!contentNode) return;

		const clone = contentNode.cloneNode(true);

		const originalControls = contentNode.querySelectorAll('input, textarea, select');
		const clonedControls = clone.querySelectorAll('input, textarea, select');
		originalControls.forEach((orig, i) => {
			const cloned = clonedControls[i];
			if (!cloned) return;

			if (orig.tagName === 'SELECT') {
				const selectedOption = orig.options[orig.selectedIndex];
				const span = document.createElement('span');
				span.className = cloned.className;
				span.textContent = selectedOption?.value ? selectedOption.text : '';
				cloned.replaceWith(span);
			} else if (orig.type === 'checkbox' || orig.type === 'radio') {
				if (orig.checked) cloned.setAttribute('checked', 'checked');
				else cloned.removeAttribute('checked');
			} else if (cloned.tagName === 'TEXTAREA') {
				cloned.textContent = orig.value ?? '';
			} else {
				cloned.setAttribute('value', orig.value ?? '');
			}
		});

		clone.querySelector('#form-sidebar')?.remove();
		clone.querySelector('select.form-select.ms-auto')?.remove();
		clone.querySelector('.PS_Qus_Card_footer')?.remove();
		clone.querySelector('.Form_footer')?.remove();
		clone.querySelectorAll('.Department_Namebox').forEach((el) => {
			if (el.textContent.includes('Expiry')) el.remove();
		});
		clone.querySelectorAll('.comment-timestamp').forEach((el) => el.remove());
		clone.querySelectorAll('.text-end.text-secondary').forEach((el) => el.remove());

		clone.querySelectorAll('.DataTableView button').forEach((btn) => btn.remove());

		const tableHtmlMap = {};
		Array.from(clone.querySelectorAll('table')).forEach((table, i) => {
			if (table.parentElement?.closest('table')) return;
			table.querySelectorAll('input').forEach((input) => {
				const span = document.createElement('span');
				span.textContent = input.getAttribute('value') ?? '';
				input.replaceWith(span);
			});
			table.setAttribute('border', '1');
			table.setAttribute('cellpadding', '4');
			table.setAttribute('cellspacing', '0');
			table.setAttribute('style', 'border-collapse:collapse;width:100%');
			const token = `@@WORD_TABLE_${i}@@`;
			tableHtmlMap[token] = table.outerHTML;
			const placeholder = document.createElement('p');
			placeholder.textContent = token;
			table.replaceWith(placeholder);
		});

		clone.querySelectorAll('.PS_inner_01').forEach((block) => {
			const textCell = block.querySelector('.PSinner_Qus_View_box02 .d-flex > div');
			const answerEl = block.querySelector('.PSinner_Option_View_box02');
			if (textCell && answerEl) textCell.appendChild(answerEl);
		});

		clone.querySelectorAll('.d-flex').forEach((el) => {
			const table = document.createElement('table');
			table.setAttribute('cellpadding', '0');
			table.setAttribute('cellspacing', '0');
			table.style.borderCollapse = 'collapse';
			const tr = document.createElement('tr');
			Array.from(el.children).forEach((child) => {
				const td = document.createElement('td');
				td.style.verticalAlign = 'top';
				td.style.padding = '0';
				td.appendChild(child);
				tr.appendChild(td);
			});
			table.appendChild(tr);
			el.replaceWith(table);
		});
		clone.querySelectorAll('.me-1').forEach((el) => {
			const tab = document.createElement('span');
			tab.setAttribute('style', 'mso-tab-count:1');
			tab.innerHTML = '&nbsp;';
			el.appendChild(tab);
		});

		const htmlHeader = "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>" + (formTitle || 'Form') + "</title><style>body { font-size: 14pt; } h5 { font-size: 13.5pt; } h6 { font-size: 12pt; } small { font-size: 10pt; }</style></head><body>";
		const htmlFooter = "</body></html>";
		let bodyHTML = clone.innerHTML;
		Object.entries(tableHtmlMap).forEach(([token, html]) => {
			bodyHTML = bodyHTML.replace(token, () => html);
		});
		const sourceHTML = htmlHeader + bodyHTML + htmlFooter;

		const blob = new Blob(['﻿', sourceHTML], { type: 'application/msword' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `${formTitle || 'form'}.doc`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	return (
		<div className='main_wizard_head'>
			<div className={showWizard ? 'main_wizard_head-inner' : 'main_wizard_head-inner'}>

				{
					showWizard
						? <>
							<div className='main_wizard_head-inner_01 showWizard_01'>
								<b className='m-0'>{formTitle}</b>
								<div className="page_toggleBox edit_preview_toggle">
									<div className="toggle-switch">
										<Form.Check
											inline
											type="radio"
											id="form_edit_mode"
											label={
												<>
													<MdEditNote className='me-1' />
													Build
												</>
											}
											checked={window.location.href.includes('form_edit')}
											onChange={() => {
												if (!window.location.href.includes('form_edit')) {
													goto('form_edit');
												}
											}}
										/>
									</div>
									<div className="toggle-switch">
										<Form.Check
											inline
											type="radio"
											id="form_preview_mode"
											label={
												<>
													<BsEye className='me-1' />
													Preview
												</>
											}
											checked={window.location.href.includes('form_preview')}
											onChange={() => {
												if (!window.location.href.includes('form_preview')) {
													goto('form_preview');
												}
											}}
										/>
									</div>
								</div>
							</div>

						</>

						:
						<>
							<div className='main_wizard_head-inner_01 1'>
								<b className='m-0'>{formTitle}</b>

								<div className='d-flex'>

									<div className='progress-count-wrap'>
										<div className='progress-count-wrap__inner'>
											<span className='progress-count-wrap__percent'>
												{(answeredQuestionCount && totalQuestionCount)
													? Math.round((answeredQuestionCount / totalQuestionCount) * 100)
													: 0}%
											</span>
											<ProgressBar
												now={(answeredQuestionCount / totalQuestionCount) * 100}
												aria-label="Form completion progress"
												aria-valuenow={(answeredQuestionCount && totalQuestionCount)
													? Math.round((answeredQuestionCount / totalQuestionCount) * 100)
													: 0}
												aria-valuemin={0}
												aria-valuemax={100}
											/>
										</div>
										<div className='progress-count-wrap__answered'>
											<BiCheck className='progress-count-wrap__answered-icon' aria-hidden="true" />
											<span className='fw-bold text-success'>{answeredQuestionCount}</span>/
											<span className='fw-bold'>{totalQuestionCount}</span> Answered

										</div>
									</div>


									{
										(isOpenForm || isApprovalForm) && <div className="ms-2 d-flex">
											<div className="ms-2 page_toggleBox">
												<OverlayTrigger
													placement="top"
													overlay={<Tooltip id="view-toggle-modern">Modern</Tooltip>}>
													<div className="toggle-switch">
														<Form.Check
															inline
															label={
																<>
																	<BsViewStacked aria-hidden="true" />
																	<span className="visually-hidden">Section view</span>
																</>
															}
															type="radio"
															id="inline-radio-5"
															name="form-view-toggle"
															value={0}
															aria-label="Section view"
															checked={isSectionViewChecked()}
															onChange={(e) => {
																setSidebarDisplay?.(true);
																if (isSubmittedResponseView()) {
																	navigateFormView?.();
																} else {
																	if (accessToken) {
																		navigate(`/form_builder/form_submit/${paramObj}`);
																	} else {
																		navigate(`/form_builder/open-form/submit/${token}`);
																	}
																}
															}}
														/>
													</div>
												</OverlayTrigger>
												<OverlayTrigger
													placement="top"
													overlay={<Tooltip id="view-toggle-classic">Classic</Tooltip>}>
													<div className="toggle-switch">
														<Form.Check
															inline
															label={
																<>
																	<BsTextLeft aria-hidden="true" />
																	<span className="visually-hidden">Consolidated view</span>
																</>
															}
															type="radio"
															id="inline-radio-6"
															name="form-view-toggle"
															value={1}
															aria-label="Consolidated view"
															checked={isConsolidatedViewChecked()}
															onChange={(e) => {
																setSidebarDisplay?.(false);
																if (isSubmittedResponseView()) {
																	navigateFormView?.();
																} else {
																	if (accessToken) {
																		navigate(`/form_builder/form_submit/sp/${paramObj}`);
																	} else {
																		navigate(`/form_builder/open-form/submit/sp/${token}`);
																	}
																}
															}}
														/>
													</div>
												</OverlayTrigger>
											</div>


											{isConsolidatedViewChecked() && (
												<OverlayTrigger
													placement="top"
													overlay={<Tooltip id="download-word-doc">Download as Word</Tooltip>}>
													<button
														type="button"
														className="ms-2 download-word-btn"
														aria-label="Download as Word document"
														onClick={handleDownloadWordDoc}
													>
														<AiOutlineFileWord size={18} />
													</button>
												</OverlayTrigger>
											)}
										</div>

									}
								</div>
							</div>



						</>
				}
			</div>
		</div >
	);
};

export default FormHeader;